// src/pages/Moderation/ChannelsViolationsPage/ui/ChannelViolationsDrawer/ChannelViolationsDrawer.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { GetFlagsResponse, ReportInfo } from 'redux/reducers/moderation/types';
import { ELinksName } from 'services/constants/list-links-sidebar';
import { decodeRestrictionValue } from 'untils/decodeRestrictions';
import { ModerationChannel } from '../model/types';
import './ChannelViolationsDrawer.scss';

import { Loader } from 'components/Loader';
import { fetchWithAuthentication } from 'untils/getFile';
import { getProtectedImageBlob } from 'untils/imageCache';
import { onHttp } from 'untils/onHttp';
import { safeJsonParse } from 'untils/safeJsonParse';

type UiMedia = {
  kind: 'photo' | 'video' | 'gif' | 'document' | 'audio' | 'unknown';
  url?: string;
  thumbUrl?: string;
  fileName?: string;
};

type UiContent = {
  text?: string;
  url?: string;
  media: UiMedia[];
};

type Props = {
  channel: ModerationChannel;
  flags: GetFlagsResponse | null;

  // ✅ додали
  reportInfo?: ReportInfo | null;
  reportLoading?: boolean;
};

const buildMediaUrl = (type: 'photo' | 'document', accessHash?: string) => {
  if (!accessHash) return undefined;
  return `${onHttp()}/storage/${type}/${accessHash}`;
};

const extractReportContent = (reportInfo: ReportInfo): UiContent => {
  const textFromMessage = reportInfo?.message?.message ?? '';
  const messageDataRaw = reportInfo?.message?.message_data ?? '';

  const uiContent: UiContent = {
    text: textFromMessage || undefined,
    url: undefined,
    media: [],
  };

  if (!messageDataRaw) return uiContent;

  let parsed: any = null;
  try {
    parsed = safeJsonParse(messageDataRaw);
  } catch {
    return uiContent;
  }

  const urlCandidate = parsed?.link ?? parsed?.url ?? parsed?.webpage?.url ?? undefined;
  if (urlCandidate) uiContent.url = urlCandidate;

  const mediaBlock = parsed?.media;

  const photo = mediaBlock?.photo_FLAGPHOTO;
  if (photo?.access_hash) {
    const photoUrl = buildMediaUrl('photo', photo.access_hash);
    uiContent.media.push({
      kind: 'photo',
      url: photoUrl,
      thumbUrl: photoUrl,
      fileName: 'photo',
    });
  }

  const document = mediaBlock?.document;
  if (document?.access_hash) {
    const mime: string | undefined = document?.mime_type;

    const isVideo = mime?.startsWith('video/');
    const isGif = mime === 'image/gif';
    const isImage = mime?.startsWith('image/');

    const documentUrl = buildMediaUrl('document', document.access_hash);

    const kind: UiMedia['kind'] = isVideo
      ? 'video'
      : isGif
      ? 'gif'
      : isImage
      ? 'photo'
      : 'document';

    uiContent.media.push({
      kind,
      url: documentUrl,
      thumbUrl: documentUrl,
      fileName: document?.attributes?.find((a: any) => a?.file_name)?.file_name ?? 'document',
    });
  }

  return uiContent;
};

export const ChannelViolationsDrawer: React.FC<Props> = ({
  channel,
  flags,
  reportInfo,
  reportLoading,
}) => {
  const [mediaSrc, setMediaSrc] = useState<string>();
  const [mediaLoading, setMediaLoading] = useState(false);

  const selectedContent = useMemo(() => {
    if (!reportInfo) return null;
    return extractReportContent(reportInfo);
  }, [reportInfo]);

  const firstMedia = selectedContent?.media?.[0];

  useEffect(() => {
    if (!firstMedia?.url) {
      setMediaSrc(undefined);
      return;
    }

    const token = typeof window !== 'undefined' ? localStorage.getItem('token') || '' : '';

    let alive = true;
    let objectUrl: string | undefined;

    getProtectedImageBlob(firstMedia.url, token, fetchWithAuthentication, setMediaLoading)
      .then((blob) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setMediaSrc(objectUrl);
      })
      .catch(() => {
        if (alive) setMediaSrc(undefined);
      });

    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [firstMedia?.url]);

  return (
    <aside className="cv-drawer">
      <div className="cv-drawer__title">Channel details</div>

      <div className="cv-drawer__row">
        <span className="cv-drawer__k">Title</span>
        <span className="cv-drawer__v">{channel.title || '—'}</span>
      </div>

      <div className="cv-drawer__row">
        <span className="cv-drawer__k">Category</span>
        <span className="cv-drawer__v">{channel.category || '—'}</span>
      </div>

      <div className="cv-drawer__row">
        <span className="cv-drawer__k">Link</span>
        <span className="cv-drawer__v">{channel.link || '—'}</span>
      </div>

      <div className="cv-drawer__row">
        <span className="cv-drawer__k">ID</span>
        <span className="cv-drawer__v">{channel.id}</span>
      </div>

      <div className="cv-drawer__row">
        <span className="cv-drawer__k">Created</span>
        <span className="cv-drawer__v">{channel.created_at || '—'}</span>
      </div>

      <div className="cv-drawer__row" style={{ alignItems: 'flex-start' }}>
        <span className="cv-drawer__k">Restrictions</span>
        <span className="cv-drawer__v" style={{ textAlign: 'right' }}>
          {(channel.restrictions ?? []).length ? (
            <span>
              {(channel.restrictions ?? []).map((r) => (
                <span key={r.restriction} style={{ display: 'block' }}>
                  {flags ? decodeRestrictionValue(r.restriction, flags.data) : null}
                </span>
              ))}
            </span>
          ) : (
            '—'
          )}
        </span>
      </div>

      {/* ✅ Media */}
      <div className="cv-drawer__row" style={{ alignItems: 'flex-start' }}>
        <span className="cv-drawer__k">Media</span>
        <span className="cv-drawer__v" style={{ textAlign: 'right' }}>
          {reportLoading ? (
            <Loader />
          ) : !firstMedia?.url ? (
            '—'
          ) : mediaLoading ? (
            <Loader />
          ) : firstMedia.kind === 'video' ? (
            <div className="cv-drawer__media">
              <video
                className="cv-drawer__img"
                src={mediaSrc}
                controls
                playsInline
                preload="metadata"
              />
            </div>
          ) : (
            <div className="cv-drawer__media">
              <img
                className="cv-drawer__img"
                src={mediaSrc}
                alt={firstMedia.fileName}
                loading="lazy"
              />
            </div>
          )}
        </span>
      </div>

      <div style={{ marginTop: 12 }}>
        <Link
          className="cv-table__open"
          to={`${ELinksName.CHANNEL_DETAIL_USER}/${channel.id}/all-channels`}
        >
          Go to channel page
        </Link>
      </div>
    </aside>
  );
};
